import { BonusGameMappingsQueryKey } from '../../queryKey';
import {
  UseBonusGameMappingsProps,
  UseBonusGameMappingsQueryKey,
} from './props';
import { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@starsoft/common/services/query-client';
import { fetchBonusGameMappings } from './service';
import {
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';
import { BonusGameMapping } from '@/models/bonus/game/mapping';
import { GenericError, PaginatedResponse } from '@starsoft/common/models';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

export function useBonusGameMappings({
  bonusId,
  enabled = true,
}: UseBonusGameMappingsProps) {
  const queryKey: UseBonusGameMappingsQueryKey = [
    BonusGameMappingsQueryKey.Primary,
    {
      bonusId,
      limit: 20,
    },
  ];

  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();
  useInfiniteQueryReset(queryKey);

  const { data, isPending, fetchStatus, ...infiniteQuery } = useInfiniteQuery<
    PaginatedResponse<BonusGameMapping>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<BonusGameMapping>>,
    UseBonusGameMappingsQueryKey
  >({
    queryKey,
    getNextPageParam,
    initialPageParam: 1,
    getPreviousPageParam,
    enabled: enabled && !!bonusId,
    queryFn: fetchBonusGameMappings,
  });

  const bonusGameMappings: BonusGameMapping[] = useMemo(
    () => data?.pages?.map(page => page?.data)?.flat(),
    [data],
  );

  return {
    ...infiniteQuery,
    bonusGameMappings,
    metadata: data?.pages?.[0]?.metadata,
    isPending: isPending && fetchStatus !== 'idle',
  };
}
